/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import redux_flowImg from 'assets/images/redux-flow.gif'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - Асинхронность в Redux</h2>
    <p className={styles.description}>
      На иллюстрации ниже изображена полная схема работы Redux в приложении при асинхронных запросах.{' '}
    </p>
    <img className={styles.img} src={redux_flowImg} alt="redux-flow" />
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - Асинхронные действия</h3>
      <p className={styles.description}>
        <b>Redux</b> не предоставляет функционала для отправки асинхронных действий, эту задачу решают прослойки. Есть
        много готовых решений: для простых асинхронных операций подойдет{' '}
        <code className={styles.code}>redux-promise</code>, для средней сложности{' '}
        <code className={styles.code}>redux-thunk</code>, а для очень сложных и запутанных{' '}
        <code className={styles.code}>redux-saga</code> или <code className={styles.code}>redux-observable</code>.
      </p>
      <p className={styles.description}>
        Для начала необходимо научиться писать асинхронные <code className={styles.code}>action creators</code>. Это
        функции которые вместо объекта-действия возвращают функцию. В компьютерных науках это называется{' '}
        <code className={styles.code}>thunk</code>.{' '}
      </p>
      <CopyBlock
        text={`const asyncActionCreator = args => dispatch => {};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Когда <code className={styles.code}>action creators</code> возвращает функцию, эта функция будет выполняться
        прослойкой. Такая функция не должна быть чистой, поэтому она может иметь побочные эффекты, в том числе
        выполнение асинхронных HTTP-запросов. В ее теле также могут быть отправлены другие сихронные действия.{' '}
      </p>
      <CopyBlock
        text={`const asyncActionCreator = args => dispatch => {
  fetch('some url')
    .then(r => r.json())
    .then(data => {
      dispatch({
        type: 'FETCH_SUCCESS',
        payload: data,
      });
    });
};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - thunk middleware</h3>
      <p className={styles.description}>
        Напишем прослойку <code className={styles.code}>thunk</code>, которая умеет обрабатывать асинхронные действия.
        Если действие это функция, она будет вызвана и аргументами ей будут переданы{' '}
        <code className={styles.code}>dispatch</code> и <code className={styles.code}>getState</code>, тем самым
        позволяя использовать <code className={styles.code}>dispatch</code> в теле действия. В противном случае, если
        это обычный объект, действие будет отправлено дальше по цепочке прослоек.{' '}
      </p>
      <CopyBlock
        text={`const thunk = ({ dispatch, getState }) => next => action =>
  typeof action === 'function' ? action(dispatch, getState) : next(action);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <a className={styles.link} href="https://github.com/reduxjs/redux-thunk" target="_blank">
          Репозиторий redux-thunk
        </a>
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.3 - HTTP-запросы</h3>
      <p className={styles.description}>
        При асинхроннах операциях зачастую необходимо отображать лоадеры и обрабатывать ошибки. В таких случаях можно
        использовать следующую схему действий.{' '}
      </p>
      <p className={styles.description}>
        Начальное состояние может выглядеть так, с полями флага загрузки и хранения ошибки.{' '}
      </p>
      <CopyBlock
        text={`{
  notes: {
    items: [],
    loading: false,
    error: null
  }
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Тогда асинхронное действие может выглядеть так. </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          При <code className={styles.code}>notes/FETCH_START</code> в поле <code className={styles.code}>loading</code>{' '}
          записывается <code className={styles.code}>true</code>, а при{' '}
          <code className={styles.code}>notes/FETCH_SUCCESS</code> или <code>notes/FETCH_FAILURE</code> наоборот{' '}
          <code className={styles.code}>false</code>
        </li>
        <li className={styles.list__item}>
          При <code className={styles.code}>notes/FETCH_FAILURE</code> в поле <code className={styles.code}>error</code>{' '}
          записывается объект ошибки
        </li>
        <li className={styles.list__item}>
          При <code className={styles.code}>notes/FETCH_SUCCESS</code> в поле <code className={styles.code}>items</code>{' '}
          записываются данные
        </li>
      </ul>
      <CopyBlock
        text={`const fetchStart = () => ({
  type: 'notes/FETCH_START',
});

const fetchSuccess = data => ({
  type: 'notes/FETCH_SUCCESS',
  payload: data,
});

const fetchFailure = error => ({
  type: 'notes/FETCH_FAILURE',
  payload: error,
});

const asyncActionCreator = args => dispatch => {
  dispatch(fetchStart());

  fetch('some url')
    .then(r => r.json())
    .then(data => dispatch(fetchSuccess(data)))
    .catch(err => dispatch(fetchFailure(err)));
};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
